import { Component, ElementRef, OnDestroy, ViewChild, signal, input, output } from '@angular/core';
import { AppSvgIconComponent } from '@vallift/ngl';

@Component({
  selector: 'app-image-picker',
  imports: [AppSvgIconComponent],
  templateUrl: './image-picker.html',
  styleUrl: './image-picker.css',
})
export class ImagePicker implements OnDestroy {
  @ViewChild('fileInput')
  fileInput!: ElementRef<HTMLInputElement>;

  @ViewChild('video')
  video!: ElementRef<HTMLVideoElement>;
  readonly label = input<string>('');
  readonly helpText = input<string>('');
  readonly required = input<boolean>(false);
  readonly maxFileSize = input<number | null>(null);
  readonly allowCamera = input<boolean>(true);
  readonly filesChange = output<File[]>();

  imagePreviews = signal<SelectedImage[]>([]);

  selectedImages = signal<File[]>([]);

  showImageOptions = signal(false);

  showCamera = signal(false);

  cameraStream: MediaStream | null = null;

  showPermissionPopup = signal(false);

  cameraPermissionDenied = signal(false);

  cameraError = signal<string | null>(null);

  private emitFilesChange(): void {
    this.filesChange.emit(this.selectedImages());
  }

  openImageOptions(): void {
    if (this.allowCamera()) {
      this.takePhoto();
      return;
    }

    this.chooseFromGallery();
  }

  closeImageOptions(): void {
    this.showImageOptions.set(false);
  }

  chooseFromGallery(): void {
    this.closeImageOptions();
    this.fileInput.nativeElement.click();
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }
    const files = Array.from(input.files).filter((file) => file.type.startsWith('image/'));
    const maxFileSize = this.maxFileSize();
    const validFiles = files.filter((file) => {
      if (maxFileSize !== null && file.size > maxFileSize) {
        this.cameraError.set(
          `${file.name} exceeds the maximum file size of ${this.formatFileSize(maxFileSize)}.`,
        );
        return false;
      }
      return true;
    });
    const newImages: SelectedImage[] = validFiles.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      preview: URL.createObjectURL(file),
    }));
    this.selectedImages.update((images) => [...images, ...validFiles]);
    this.imagePreviews.update((images) => [...images, ...newImages]);
    input.value = '';
    this.emitFilesChange();
  }

  private formatFileSize(bytes: number): string {
    if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  removeImage(id: number): void {
    this.imagePreviews.update((images) => {
      const image = images.find((item) => item.id === id);

      if (image) {
        URL.revokeObjectURL(image.preview);
      }

      return images.filter((item) => item.id !== id);
    });

    this.selectedImages.update((files) => {
      const remainingIds = new Set(this.imagePreviews().map((image) => image.file));

      return files.filter((file) => remainingIds.has(file));
    });
    this.emitFilesChange();
  }

  takePhoto(): void {
    void this.openCamera();
  }

  async openCamera(): Promise<void> {

    this.cameraPermissionDenied.set(false);

    this.cameraError.set(null);

    if (!window.isSecureContext) {
      this.showCameraError('Camera access requires a secure connection (HTTPS).');

      return;
    }


    if (!navigator.mediaDevices?.getUserMedia) {
      this.showCameraError('Camera is not supported by this browser.');

      return;
    }

    try {

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: {
            ideal: 'environment',
          },
        },
        audio: false,
      });

      this.cameraStream = stream;

      this.showCamera.set(true);

      setTimeout(() => {
        this.attachCameraStream();
      });
    } catch (error) {
      console.error('Camera error:', error);

      this.handleCameraError(error);
    }
  }

  private attachCameraStream(): void {
    if (!this.video || !this.cameraStream) {
      console.error('Video element or camera stream not available');

      return;
    }

    const videoElement = this.video.nativeElement;

    videoElement.srcObject = this.cameraStream;

    videoElement.muted = true;

    videoElement.playsInline = true;

    videoElement.autoplay = true;
  }

  private handleCameraError(error: unknown): void {
    if (!(error instanceof DOMException)) {
      this.showCameraError('Unable to access the camera. Please try again.');

      return;
    }

    switch (error.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        this.showCameraPermissionMessage();
        break;

      case 'NotFoundError':
        this.showCameraError('No camera was found on this device.');
        break;

      case 'NotReadableError':
        this.showCameraError('The camera is already being used by another application.');
        break;

      case 'OverconstrainedError':
        this.showCameraError('The requested camera is not available.');
        break;

      case 'SecurityError':
        this.showCameraError('Camera access was blocked by the browser.');
        break;

      default:
        this.showCameraError('Unable to access the camera. Please try again.');
        break;
    }
  }

  private showCameraPermissionMessage(): void {
    this.cameraPermissionDenied.set(true);

    this.showPermissionPopup.set(true);
  }

  closePermissionPopup(): void {
    this.showPermissionPopup.set(false);
  }

  private showCameraError(message: string): void {
    this.cameraError.set(message);
  }

  captureImage(): void {
    if (!this.video) {
      return;
    }

    const video = this.video.nativeElement;

    if (!video.videoWidth || !video.videoHeight) {
      console.error('Camera video is not ready');

      return;
    }

    const canvas = document.createElement('canvas');

    canvas.width = video.videoWidth;

    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');

    if (!context) {
      console.error('Unable to create canvas context');

      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          return;
        }

        const file = new File([blob], `camera-image-${Date.now()}.jpg`, {
          type: 'image/jpeg',
        });

        const image: SelectedImage = {
          id: Date.now() + Math.random(),
          file,
          preview: URL.createObjectURL(blob),
        };

        this.selectedImages.update((images) => [...images, file]);

        this.imagePreviews.update((images) => [...images, image]);

        this.emitFilesChange();
        this.closeCamera();
      },
      'image/jpeg',
      0.9,
    );
  }

  closeCamera(): void {
    if (this.cameraStream) {
      this.cameraStream.getTracks().forEach((track) => {
        track.stop();
      });
    }

    this.cameraStream = null;

    if (this.video) {
      this.video.nativeElement.srcObject = null;
    }

    this.showCamera.set(false);
  }

  ngOnDestroy(): void {
    if (this.cameraStream) {
      this.cameraStream.getTracks().forEach((track) => {
        track.stop();
      });
    }

    this.imagePreviews().forEach((image) => {
      if (image.preview.startsWith('blob:')) {
        URL.revokeObjectURL(image.preview);
      }
    });
  }
}

interface SelectedImage {
  id: number;
  file: File;
  preview: string;
}
